import { useCallback, useEffect, useState } from "react";
import { Copy, EnvelopeSimple, Flask, Key, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const STATUS_LABELS = {
  verifica_gmail: "Verifica Gmail",
  importata: "Importata", duplicata: "Duplicata", scartata: "Scartata",
  incompleta: "Incompleta", cancellata: "Cancellazione applicata",
  cancellazione_senza_match: "Cancellazione senza match", errore_parsing: "Errore parsing",
  non_instradata: "Non instradata",
};

export default function AggregatoreSection({ readOnly }) {
  const [status, setStatus] = useState(null);
  const [imports, setImports] = useState([]);
  const [testSubject, setTestSubject] = useState("Nuova prenotazione");
  const [testText, setTestText] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/aggregator/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/aggregator/imports").then((r) => setImports(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    try {
      const r = await api.post("/aggregator/provision");
      setStatus((s) => ({ ...s, address: r.data.address }));
      toast.success("Indirizzo aggregatore generato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = () => {
    navigator.clipboard.writeText(status.address);
    toast.success("Indirizzo copiato negli appunti");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(status.gmail_code);
    toast.success("Codice copiato negli appunti");
  };

  const dismissCode = async () => {
    try {
      await api.post("/aggregator/gmail-code/dismiss");
      setStatus((s) => ({ ...s, gmail_code: null }));
      toast.success("Verifica Gmail completata");
    } catch (e) { toast.error(apiError(e)); }
  };

  const [reprocessing, setReprocessing] = useState(null);
  const reprocess = async (id) => {
    setReprocessing(id);
    try {
      const r = await api.post(`/aggregator/events/${id}/reprocess`);
      const st = r.data.status || (r.data.duplicate ? "duplicata" : null);
      if (st === "importata") toast.success("Email rielaborata: prenotazione creata in 'Da confermare'");
      else if (st === "cancellata") toast.success("Email rielaborata: prenotazione cancellata");
      else toast.info(`Rielaborazione completata: ${st || "nessuna azione"}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setReprocessing(null); }
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    setTesting(true);
    setResult(null);
    try {
      const r = await api.post("/aggregator/test-parse", { subject: testSubject, body: testText });
      setResult(r.data.parsed);
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  if (!status) return <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="aggregatore-section">
      <div>
        <h2 className="font-display text-xl font-bold text-zinc-900">Aggregatore prenotazioni</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Inoltra a un indirizzo dedicato le email di prenotazione di TheFork, Google Reserve,
          Quandoo o TripAdvisor: l'AI estrae i dati e crea la prenotazione in "Da confermare",
          con notifica automatica al ristorante.
        </p>
      </div>

      {/* Indirizzo dedicato */}
      <div className="bg-white rounded-lg border border-zinc-200 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={18} weight="bold" className="text-zinc-500" />
          <h3 className="text-sm font-bold text-zinc-900">Il tuo indirizzo di inoltro</h3>
        </div>
        {status.address ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm font-mono text-zinc-800 truncate"
              data-testid="agg-address">{status.address}</code>
            <button onClick={copy} data-testid="agg-copy"
              className="h-10 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
              <Copy size={14} weight="bold" /> Copia
            </button>
          </div>
        ) : (
          <button onClick={provision} disabled={readOnly} data-testid="agg-provision"
            className="h-10 px-4 rounded-md text-white text-xs font-bold disabled:opacity-50"
            style={{ backgroundColor: "#018BFA" }}>
            Genera indirizzo dedicato
          </button>
        )}
        <div className="text-xs text-zinc-500 space-y-1.5 leading-relaxed">
          <p><strong>Come si attiva con Gmail:</strong> Impostazioni → "Inoltro e POP/IMAP" →
            "Aggiungi indirizzo di inoltro" → incolla l'indirizzo qui sopra. Gmail invia un codice
            di verifica a questo indirizzo: <strong>apparirà automaticamente in questa pagina</strong> entro
            pochi secondi. Incollalo in Gmail per confermare, poi crea un filtro
            (es. oggetto contiene "prenotazione") con azione "Inoltra a" questo indirizzo.</p>
          <p><strong>Con TheFork Manager:</strong> in alternativa puoi impostare direttamente questo
            indirizzo come email di notifica delle prenotazioni.</p>
          <p data-testid="agg-inbound-state">
            Stato ricezione: {status.inbound_configured
              ? <span className="text-emerald-600 font-semibold">attiva</span>
              : <span className="text-amber-600 font-semibold">in attesa di configurazione dominio (lato Dishenda)</span>}
          </p>
        </div>
      </div>

      {/* Codice verifica inoltro Gmail */}
      {status.gmail_code && (
        <div className="bg-amber-50 rounded-lg border border-amber-300 p-5 space-y-2.5" data-testid="agg-gmail-card">
          <div className="flex items-center gap-2">
            <Key size={18} weight="bold" className="text-amber-600" />
            <h3 className="text-sm font-bold text-amber-900">Codice di verifica Gmail ricevuto</h3>
          </div>
          <p className="text-xs text-amber-800 leading-relaxed">
            Gmail ha inviato questo codice per confermare l'inoltro automatico. Incollalo in
            Gmail → Impostazioni → "Inoltro e POP/IMAP" → Verifica, poi tocca "Fatto".
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-300 rounded-md px-3 py-2.5 text-lg font-mono font-bold text-amber-900 tracking-[0.3em] text-center"
              data-testid="agg-gmail-code">{status.gmail_code}</code>
            <button onClick={copyCode} data-testid="agg-gmail-copy"
              className="h-11 px-3 rounded-md bg-amber-600 text-white text-xs font-bold flex items-center gap-1.5 hover:bg-amber-700 transition-colors">
              <Copy size={14} weight="bold" /> Copia
            </button>
            <button onClick={dismissCode} data-testid="agg-gmail-dismiss"
              className="h-11 px-3 rounded-md border border-amber-400 text-amber-800 text-xs font-bold hover:bg-amber-100 transition-colors">
              Fatto
            </button>
          </div>
        </div>
      )}

      {/* Test del parser AI */}
      <div className="bg-white rounded-lg border border-zinc-200 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Flask size={18} weight="bold" className="text-zinc-500" />
          <h3 className="text-sm font-bold text-zinc-900">Prova il parser AI</h3>
        </div>
        <p className="text-xs text-zinc-500">Incolla una vera email di prenotazione per vedere cosa estrae l'AI. Non crea prenotazioni.</p>
        <input value={testSubject} onChange={(e) => setTestSubject(e.target.value)}
          placeholder="Oggetto email" data-testid="agg-test-subject"
          className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
        <textarea value={testText} onChange={(e) => setTestText(e.target.value)} rows={7}
          placeholder="Incolla qui il testo completo della email di TheFork / Google / Quandoo..."
          data-testid="agg-test-body"
          className="w-full px-3 py-2.5 rounded-md border border-zinc-300 text-sm bg-white font-mono" />
        <button onClick={runTest} disabled={testing || !testText.trim() || readOnly} data-testid="agg-test-run"
          className="h-10 px-4 rounded-md text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5"
          style={{ backgroundColor: "#018BFA" }}>
          <Robot size={14} weight="bold" /> {testing ? "Analisi in corso..." : "Analizza con AI"}
        </button>
        {result && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4" data-testid="agg-test-result">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <span className="text-zinc-500">Azione</span><span className="font-semibold" data-testid="agg-res-action">{result.action}</span>
              <span className="text-zinc-500">Fonte</span><span className="font-semibold" data-testid="agg-res-source">{result.source}</span>
              <span className="text-zinc-500">Nome</span><span className="font-semibold" data-testid="agg-res-name">{result.first_name} {result.last_name}</span>
              <span className="text-zinc-500">Telefono</span><span className="font-semibold">{result.phone || "—"}</span>
              <span className="text-zinc-500">Email</span><span className="font-semibold">{result.email || "—"}</span>
              <span className="text-zinc-500">Data</span><span className="font-semibold" data-testid="agg-res-date">{result.date || "—"}</span>
              <span className="text-zinc-500">Ora</span><span className="font-semibold" data-testid="agg-res-time">{result.time || "—"}</span>
              <span className="text-zinc-500">Coperti</span><span className="font-semibold" data-testid="agg-res-party">{result.party_size || "—"}</span>
              <span className="text-zinc-500">Note</span><span className="font-semibold">{result.notes || "—"}</span>
            </div>
          </div>
        )}
      </div>

      {/* Importazioni recenti */}
      <div className="bg-white rounded-lg border border-zinc-200 p-5">
        <h3 className="text-sm font-bold text-zinc-900 mb-3">Email importate di recente ({status.imports_count})</h3>
        {imports.length === 0 ? (
          <p className="text-xs text-zinc-400" data-testid="agg-imports-empty">Nessuna email importata finora.</p>
        ) : (
          <div className="space-y-2" data-testid="agg-imports">
            {imports.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 text-xs border-b border-zinc-100 pb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-zinc-800 truncate">{i.subject || "(senza oggetto)"}</div>
                  <div className="text-zinc-400">{i.sender} · {new Date(i.received_at).toLocaleString("it-IT")}</div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {["scartata", "non_instradata", "errore_ai"].includes(i.status) && (
                    <button onClick={() => reprocess(i.id)} disabled={reprocessing === i.id}
                      data-testid={`agg-reprocess-${i.id}`}
                      className="px-2.5 py-1 rounded-md border border-sky-300 text-sky-700 font-bold hover:bg-sky-50 transition-colors disabled:opacity-50">
                      {reprocessing === i.id ? "Rielaboro..." : "Rielabora"}
                    </button>
                  )}
                  <span className={`px-2 py-1 rounded-full font-bold ${
                    i.status === "importata" ? "bg-emerald-100 text-emerald-700" :
                    i.status === "cancellata" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}>
                    {STATUS_LABELS[i.status] || i.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
